import React, { useState, useEffect, useCallback } from 'react';
import * as Icons from 'lucide-react';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8092' : `${window.location.protocol}//${window.location.hostname}:8092`;

const UserWASettings = () => {
  const [activeTab, setActiveTab] = useState('waba');
  const [wabaAccount, setWabaAccount] = useState(null);
  const [chatSettings, setChatSettings] = useState(null);
  const [templateSettings, setTemplateSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const [wabaRes, chatRes, tplRes] = await Promise.all([
        fetch(`${API_BASE}/api/wa/settings/waba`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/wa/settings/chat`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/wa/settings/template`, { headers }).catch(() => null),
      ]);
      if (wabaRes?.ok) { const d = await wabaRes.json(); setWabaAccount(d.account || null); }
      if (chatRes?.ok) { const d = await chatRes.json(); setChatSettings(d.settings || null); }
      if (tplRes?.ok) { const d = await tplRes.json(); setTemplateSettings(d.settings || null); }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const tabs = [
    { key: 'waba', label: 'Manage WABA', icon: 'Building2' },
    { key: 'chat', label: 'Chat Settings', icon: 'MessageCircle' },
    { key: 'template', label: 'Template Settings', icon: 'FileText' },
  ];

  const getIcon = (name, size = 18) => {
    const Icon = Icons[name];
    return Icon ? <Icon style={{ width: size, height: size }} /> : null;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
            <Icons.Settings className="w-5 h-5 text-white" />
          </div>
          WhatsApp Settings
        </h1>
        <p className="text-sm text-gray-500 mt-1">Configure your WhatsApp Business Account settings</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100">
          {tabs.map((tab) => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-all border-b-2"
              style={{
                borderColor: activeTab === tab.key ? '#25D366' : 'transparent',
                color: activeTab === tab.key ? '#25D366' : '#6b7280',
                background: activeTab === tab.key ? 'rgba(37, 211, 102, 0.04)' : 'transparent',
              }}
            >
              {getIcon(tab.icon, 16)}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <Icons.Loader2 className="w-8 h-8 animate-spin mx-auto text-green-500 mb-3" />
              <p className="text-sm text-gray-500">Loading settings...</p>
            </div>
          ) : (
            <>
              {/* Manage WABA */}
              {activeTab === 'waba' && (
                <div className="space-y-6">
                  {wabaAccount ? (
                    <>
                      <div className="p-5 rounded-xl border border-green-200 bg-green-50/30">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                              <Icons.MessageCircle className="w-7 h-7 text-white" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">{wabaAccount.name || 'My WABA'}</h3>
                              <p className="text-sm text-gray-500">WABA ID: {wabaAccount.waba_id || 'N/A'}</p>
                              <p className="text-xs text-gray-400 mt-0.5">Business ID: {wabaAccount.business_id || 'N/A'} | Tier: {wabaAccount.messaging_limit_tier || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-100 text-green-700">
                              <Icons.CheckCircle className="w-3.5 h-3.5" />
                              {(wabaAccount.status || 'active').replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="space-y-4">
                          <h3 className="font-semibold text-gray-900">Business Profile</h3>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Business Name</label>
                            <input type="text" defaultValue={wabaAccount.name || ''} className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                            <textarea rows={3} defaultValue={wabaAccount.description || ''} className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Industry</label>
                            <select defaultValue={wabaAccount.business_vertical || ''} className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400">
                              <option value="">Select industry...</option>
                              <option>Technology</option>
                              <option>E-commerce</option>
                              <option>Healthcare</option>
                              <option>Education</option>
                              <option>Finance</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Website</label>
                            <input type="url" defaultValue={wabaAccount.website || ''} placeholder="https://yourbusiness.com" className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400" />
                          </div>
                        </div>
                        <div className="space-y-4">
                          <h3 className="font-semibold text-gray-900">Account Info</h3>
                          <div className="space-y-3">
                            {[
                              { label: 'WABA ID', value: wabaAccount.waba_id },
                              { label: 'Business ID', value: wabaAccount.business_id },
                              { label: 'Namespace', value: wabaAccount.namespace },
                              { label: 'Quality Rating', value: wabaAccount.quality_rating },
                              { label: 'Messaging Limit', value: wabaAccount.messaging_limit_tier },
                              { label: 'Onboarding Method', value: wabaAccount.onboarding_method },
                            ].map((item) => (
                              <div key={item.label} className="flex items-center justify-between p-3 rounded-xl border border-gray-100">
                                <span className="text-sm text-gray-600">{item.label}</span>
                                <span className="text-sm font-medium text-gray-900">{item.value || 'N/A'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-3 pt-2">
                        <button className="px-5 py-2.5 rounded-xl text-white font-medium text-sm" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                          Save Changes
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, rgba(37, 211, 102, 0.1), rgba(18, 140, 126, 0.1))' }}>
                        <Icons.Building2 className="w-10 h-10" style={{ color: '#25D366' }} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Connect Your WhatsApp Business Account</h3>
                      <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
                        Connect your WhatsApp Business Account to start sending messages, managing templates, and engaging with customers.
                      </p>
                      <button className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-medium text-sm hover:shadow-lg transition-all" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                        <Icons.Link className="w-4 h-4" />
                        Connect via Embedded Signup
                      </button>
                      <p className="text-xs text-gray-400 mt-3">You'll be redirected to Facebook to authorize your WhatsApp Business Account</p>
                    </div>
                  )}
                </div>
              )}

              {/* Chat Settings */}
              {activeTab === 'chat' && (
                <div className="space-y-6">
                  {!wabaAccount ? (
                    <div className="text-center py-8">
                      <Icons.MessageCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">Connect a WABA account first to configure chat settings.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h3 className="font-semibold text-gray-900">General Chat Settings</h3>
                        {[
                          { label: 'Auto-reply on first message', key: 'auto_reply_enabled', enabled: chatSettings?.auto_reply_enabled ?? true },
                          { label: 'Show typing indicator', key: 'show_typing_indicator', enabled: chatSettings?.show_typing_indicator ?? true },
                          { label: 'Mark messages as read automatically', key: 'auto_mark_read', enabled: chatSettings?.auto_mark_read ?? false },
                          { label: 'Enable chatbot for off-hours', key: 'chatbot_off_hours', enabled: chatSettings?.chatbot_off_hours ?? true },
                          { label: 'Allow file attachments', key: 'allow_attachments', enabled: chatSettings?.allow_attachments ?? true },
                          { label: 'Enable message reactions', key: 'enable_reactions', enabled: chatSettings?.enable_reactions ?? true },
                        ].map((setting) => (
                          <div key={setting.key} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 hover:border-green-200 transition-all">
                            <span className="text-sm text-gray-700">{setting.label}</span>
                            <button style={{
                              background: setting.enabled ? '#25D366' : '#d1d5db',
                              width: '40px', height: '22px', borderRadius: '11px', position: 'relative',
                            }}>
                              <div className="absolute top-0.5 w-[18px] h-[18px] rounded-full bg-white shadow-sm" style={{
                                left: setting.enabled ? '20px' : '2px',
                              }} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-4">
                        <h3 className="font-semibold text-gray-900">Auto-Reply Configuration</h3>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Welcome Message</label>
                          <textarea rows={3} defaultValue={chatSettings?.welcome_message || ''} placeholder="Enter welcome message..." className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Away Message</label>
                          <textarea rows={3} defaultValue={chatSettings?.away_message || ''} placeholder="Enter away message..." className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Business Hours</label>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <span className="text-xs text-gray-500">Start</span>
                              <input type="time" defaultValue={chatSettings?.business_hours_start || '09:00'} className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm" />
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">End</span>
                              <input type="time" defaultValue={chatSettings?.business_hours_end || '18:00'} className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm" />
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Session Timeout (minutes)</label>
                          <input type="number" defaultValue={chatSettings?.session_timeout_minutes || 30} className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400" />
                        </div>
                      </div>
                    </div>
                  )}
                  {wabaAccount && (
                    <button className="px-5 py-2.5 rounded-xl text-white font-medium text-sm" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                      Save Chat Settings
                    </button>
                  )}
                </div>
              )}

              {/* Template Settings */}
              {activeTab === 'template' && (
                <div className="space-y-6">
                  {!wabaAccount ? (
                    <div className="text-center py-8">
                      <Icons.FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">Connect a WABA account first to configure template settings.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h3 className="font-semibold text-gray-900">Template Defaults</h3>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Default Language</label>
                          <select defaultValue={templateSettings?.default_language || 'en'} className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400">
                            <option value="en">English (en)</option>
                            <option value="hi">Hindi (hi)</option>
                            <option value="gu">Gujarati (gu)</option>
                            <option value="mr">Marathi (mr)</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Default Category</label>
                          <select defaultValue={templateSettings?.default_category || 'MARKETING'} className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400">
                            <option>MARKETING</option>
                            <option>UTILITY</option>
                            <option>AUTHENTICATION</option>
                          </select>
                        </div>
                        {[
                          { label: 'Auto-submit templates for approval', key: 'auto_submit_for_approval', enabled: templateSettings?.auto_submit_for_approval ?? true },
                          { label: 'Enable template versioning', key: 'enable_versioning', enabled: templateSettings?.enable_versioning ?? true },
                          { label: 'Require approval before sending', key: 'require_approval_before_send', enabled: templateSettings?.require_approval_before_send ?? false },
                        ].map((setting) => (
                          <div key={setting.key} className="flex items-center justify-between p-3 rounded-xl border border-gray-100">
                            <span className="text-sm text-gray-700">{setting.label}</span>
                            <button style={{
                              background: setting.enabled ? '#25D366' : '#d1d5db',
                              width: '40px', height: '22px', borderRadius: '11px', position: 'relative',
                            }}>
                              <div className="absolute top-0.5 w-[18px] h-[18px] rounded-full bg-white shadow-sm" style={{
                                left: setting.enabled ? '20px' : '2px',
                              }} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {wabaAccount && (
                    <button className="px-5 py-2.5 rounded-xl text-white font-medium text-sm" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                      Save Template Settings
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserWASettings;
